import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_WIDGET_ORDER, WidgetSize, WIDGET_REGISTRY, DashboardTab, getWidgetTab } from '@/config/dashboardWidgets';
import * as dashboardConfigWrapper from '@/services/edgeFunctionWrappers/dashboardConfigWrapper';
import type { ConfigSource } from '@/services/edgeFunctionWrappers/dashboardConfigWrapper';

export interface WidgetConfig {
  id: string;
  visible: boolean;
  order: number;
  size?: WidgetSize;
}

export interface DashboardConfig {
  version: number;
  widgets: WidgetConfig[];
}

interface DashboardConfigState {
  config: DashboardConfig;
  isEditMode: boolean;
  isLoading: boolean;
  isSaving: boolean;
  configSource: ConfigSource | null;
  fallbackGymId: string | null;
  /** Salle à laquelle appartient la config actuellement persistée. */
  persistedGymId: string | null;
  /** Copie de la config à l'entrée en mode édition, pour « Annuler ». */
  editSnapshot: DashboardConfig | null;

  // Actions
  toggleWidget: (widgetId: string) => void;
  reorderWidgets: (newOrder: string[]) => void;
  resizeWidget: (widgetId: string, size: WidgetSize) => void;
  resetToDefault: () => void;
  setEditMode: (enabled: boolean) => void;
  /** Sort du mode édition en restaurant la disposition d'avant l'édition. */
  cancelEdit: () => void;

  // Database actions
  fetchConfig: (gymId: string, userId: string) => Promise<void>;
  saveConfig: (gymId: string) => Promise<boolean>;
  
  // Getters
  getVisibleWidgets: () => WidgetConfig[];
  isWidgetVisible: (widgetId: string) => boolean;
  getVisibleWidgetsForTab: (tab: DashboardTab) => WidgetConfig[];
  hasVisibleWidgetsForTab: (tab: DashboardTab) => boolean;
}

// Generate default config from DEFAULT_WIDGET_ORDER
function generateDefaultConfig(): DashboardConfig {
  return {
    version: 1,
    widgets: DEFAULT_WIDGET_ORDER.map((id, index) => ({
      id,
      visible: true,
      order: index,
      size: WIDGET_REGISTRY[id]?.defaultSize,
    })),
  };
}

/**
 * Une configuration exploitable, ou la configuration par défaut.
 *
 * `getVisibleWidgets` fait `const { widgets } = get().config; widgets.filter(…)`
 * : une `config` sans tableau `widgets` y lève un `TypeError` en plein rendu,
 * donc un tableau de bord entièrement blanc. Deux entrées ne sont pas
 * maîtrisées et doivent être filtrées ici :
 * - le `localStorage` (`migrate` renvoyait tel quel tout état déjà en v2) ;
 * - la réponse serveur (colonne JSONB, `fetchConfig`).
 * Mieux vaut retomber sur la disposition par défaut qu'un écran blanc.
 */
function ensureUsableConfig(candidate: unknown): DashboardConfig {
  const config = candidate as DashboardConfig | undefined;
  if (!config || !Array.isArray(config.widgets)) {
    console.warn('[dashboardConfig] Configuration inexploitable, repli sur la disposition par défaut:', candidate);
    return generateDefaultConfig();
  }
  return config;
}

export const useDashboardConfigStore = create<DashboardConfigState>()(
  persist(
    (set, get) => ({
      config: generateDefaultConfig(),
      isEditMode: false,
      isLoading: false,
      isSaving: false,
      configSource: null,
      fallbackGymId: null,
      persistedGymId: null,
      editSnapshot: null,

      toggleWidget: (widgetId: string) => {
        set((state) => {
          const widgets = [...state.config.widgets];
          const widgetIndex = widgets.findIndex(w => w.id === widgetId);
          
          if (widgetIndex >= 0) {
            // Toggle visibility
            widgets[widgetIndex] = {
              ...widgets[widgetIndex],
              visible: !widgets[widgetIndex].visible,
            };
          } else {
            // Add widget if not in config (newly selected)
            const maxOrder = Math.max(...widgets.map(w => w.order), -1);
            widgets.push({
              id: widgetId,
              visible: true,
              order: maxOrder + 1,
              size: WIDGET_REGISTRY[widgetId]?.defaultSize,
            });
          }

          return {
            config: {
              ...state.config,
              widgets,
            },
          };
        });
      },

      reorderWidgets: (newOrder: string[]) => {
        set((state) => {
          const widgets = state.config.widgets.map(widget => {
            const newIndex = newOrder.indexOf(widget.id);
            return {
              ...widget,
              order: newIndex >= 0 ? newIndex : widget.order,
            };
          });

          return {
            config: {
              ...state.config,
              widgets,
            },
          };
        });
      },

      resizeWidget: (widgetId: string, size: WidgetSize) => {
        set((state) => {
          const widgets = state.config.widgets.map(widget =>
            widget.id === widgetId ? { ...widget, size } : widget
          );

          return {
            config: {
              ...state.config,
              widgets,
            },
          };
        });
      },

      resetToDefault: () => {
        set({
          config: generateDefaultConfig(),
          configSource: 'default',
          fallbackGymId: null,
        });
      },

      setEditMode: (enabled: boolean) => {
        // À l'entrée en édition on fige une copie de la disposition, seule
        // façon pour « Annuler » de restaurer quelque chose.
        set((state) => ({
          isEditMode: enabled,
          editSnapshot: enabled
            ? { ...state.config, widgets: state.config.widgets.map(w => ({ ...w })) }
            : null,
        }));
      },

      cancelEdit: () => {
        set((state) => ({
          isEditMode: false,
          config: state.editSnapshot ?? state.config,
          editSnapshot: null,
        }));
      },

      fetchConfig: async (gymId: string, userId: string) => {
        // La config est persistée en localStorage : sans cette remise à zéro,
        // la disposition de la salle A restait affichée sous la session de la
        // salle B — définitivement si la lecture échouait.
        const previousGymId = get().persistedGymId;
        if (previousGymId && previousGymId !== gymId) {
          set({ config: generateDefaultConfig(), configSource: 'default', fallbackGymId: null });
        }

        set({ isLoading: true });
        try {
          console.log('📥 Fetching dashboard config for gym:', gymId);
          const response = await dashboardConfigWrapper.getDashboardConfig(gymId, userId);

          if (response.config) {
            console.log('✅ Dashboard config loaded from:', response.source);
            set({
              // La colonne JSONB n'est pas garantie bien formée.
              config: ensureUsableConfig(response.config),
              configSource: response.source,
              fallbackGymId: response.fallback_gym_id || null,
              persistedGymId: gymId,
              isLoading: false
            });
          } else {
            console.log('⚠️ No dashboard config found, using defaults');
            set({
              config: generateDefaultConfig(),
              configSource: 'default',
              fallbackGymId: null,
              persistedGymId: gymId,
              isLoading: false
            });
          }
        } catch (error) {
          // En cas d'échec on retombe sur la configuration par défaut de la
          // salle demandée plutôt que de conserver celle d'une autre salle.
          console.error('💥 Failed to fetch dashboard config:', error);
          set({
            config: generateDefaultConfig(),
            configSource: 'default',
            fallbackGymId: null,
            persistedGymId: gymId,
            isLoading: false
          });
        }
      },

      saveConfig: async (gymId: string) => {
        set({ isSaving: true });
        try {
          console.log('💾 Saving dashboard config for gym:', gymId);
          const config = get().config;
          const success = await dashboardConfigWrapper.saveDashboardConfig(gymId, config);
          
          if (success) {
            console.log('✅ Dashboard config saved successfully');
            set({
              isSaving: false,
              configSource: 'current_gym',
              fallbackGymId: null,
              persistedGymId: gymId,
              // La disposition enregistrée devient la nouvelle référence :
              // « Annuler » d'une édition ultérieure ne doit pas y revenir.
              editSnapshot: null
            });
          } else {
            set({ isSaving: false });
          }
          
          return success;
        } catch (error) {
          console.error('💥 Failed to save dashboard config:', error);
          set({ isSaving: false });
          return false;
        }
      },

      getVisibleWidgets: () => {
        const { widgets } = get().config;
        return widgets
          .filter(w => w.visible)
          .sort((a, b) => a.order - b.order);
      },

      isWidgetVisible: (widgetId: string) => {
        const widget = get().config.widgets.find(w => w.id === widgetId);
        return widget?.visible ?? false;
      },

      // `definition.tab` n'est renseigné que sur quelques widgets : on passe
      // par `getWidgetTab`, qui retombe sur la catégorie. Sans cela, l'onglet
      // « Vue d'ensemble » disparaissait alors que ses 7 KPI étaient actifs.
      getVisibleWidgetsForTab: (tab: DashboardTab) => {
        const { widgets } = get().config;
        return widgets
          .filter(w => {
            if (!w.visible) return false;
            const definition = WIDGET_REGISTRY[w.id];
            return definition ? getWidgetTab(definition) === tab : false;
          })
          .sort((a, b) => a.order - b.order);
      },

      hasVisibleWidgetsForTab: (tab: DashboardTab) => {
        const { widgets } = get().config;
        return widgets.some(w => {
          if (!w.visible) return false;
          const definition = WIDGET_REGISTRY[w.id];
          return definition ? getWidgetTab(definition) === tab : false;
        });
      },
    }),
    {
      name: 'dashboard-config-storage',
      version: 2,
      // `partialize` : seules la disposition et la salle qui la porte sont
      // persistées. `isEditMode` l'était aussi, ce qui rouvrait le mode édition
      // après un simple rechargement ; `isLoading`/`isSaving` figeaient des
      // spinners.
      partialize: (state) => ({
        config: state.config,
        persistedGymId: state.persistedGymId,
      }),
      migrate: (persisted: unknown, version: number) => {
        // v1 persistait tout l'état, `isEditMode` compris : on ne garde que la
        // disposition et on repart sans salle associée (fetchConfig tranchera).
        if (version < 2) {
          const previous = persisted as { config?: DashboardConfig } | undefined;
          return {
            config: ensureUsableConfig(previous?.config),
            persistedGymId: null,
          };
        }
        // Même en v2 l'état vient du disque : il peut avoir été tronqué ou
        // édité. On valide avant de le rendre au store.
        const current = persisted as { config?: DashboardConfig } | undefined;
        return {
          ...(current ?? {}),
          config: ensureUsableConfig(current?.config),
        };
      },
    }
  )
);
